import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../../components/ui';
import { Store, Users, DollarSign, TrendingUp, Trophy, FileText, Calendar, RefreshCw, AlertTriangle } from 'lucide-react';
import { fetchAPI } from '../../../services/apiClient';
import { diaOperativo } from '../../../utils/date';

interface BackendTicket {
  id: string;
  vendedor_id: string;
  local_id: string;
  sorteo_id: string;
  total: string;
  estado: string;
  jugadas: Array<{ estado: string }>;
}

interface BackendSorteo {
  id: string;
  nombre: string;
  estado: string;
  hora_cierre: string;
}

interface BancaSaldo {
  ventas: number;
  premios: number;
  movimientos: number;
  saldo: number;
}

interface SorteoPendiente {
  id: string;
  nombre: string;
  tipo_id: string;
  fecha: string;
  hora_cierre: string;
}

export const BancaDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<BackendTicket[]>([]);
  const [localesOperativos, setLocalesOperativos] = useState<Set<string>>(new Set());
  const [sorteos, setSorteos] = useState<BackendSorteo[]>([]);
  const [saldoBanca, setSaldoBanca] = useState<BancaSaldo>({ ventas: 0, premios: 0, movimientos: 0, saldo: 0 });
  const [pendientes, setPendientes] = useState<SorteoPendiente[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = () => {
    setLoading(true);
    const hoy = diaOperativo();
    Promise.all([
      fetchAPI<BackendTicket[]>(`/tickets?fecha=${hoy}`),
      fetchAPI<BackendSorteo[]>('/sorteos?estado=ABIERTO'),
      fetchAPI<BancaSaldo>('/banca/saldo'),
      fetchAPI<{ local_id: string; local_archivado?: boolean }[]>('/usuarios/banca/locales/resumen-vendedores'),
      fetchAPI<SorteoPendiente[]>('/sorteos/pendientes-ganador').catch(() => []),
    ])
      .then(([ticketsData, sorteosData, saldoData, localesData, pendientesData]) => {
        setTickets(ticketsData);
        setSorteos(sorteosData);
        setSaldoBanca(saldoData);
        setLocalesOperativos(new Set(localesData.filter(l => !l.local_archivado).map(l => l.local_id)));
        setPendientes(pendientesData);
      })
      .catch(err => console.error('Error cargando datos:', err))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const ticketsActivos = tickets.filter(t => t.estado !== 'ANULADO' && localesOperativos.has(t.local_id));
  const ventasHoy = ticketsActivos.reduce((sum, t) => sum + parseFloat(t.total), 0);
  const ticketsHoy = ticketsActivos.length;
  const vendedoresActivos = new Set(ticketsActivos.map(t => t.vendedor_id)).size;
  const localesActivos = new Set(ticketsActivos.map(t => t.local_id)).size;
  const jugadasActivas = ticketsActivos.reduce((sum, t) => sum + t.jugadas.filter(j => j.estado === 'ACTIVA').length, 0);
  const sorteosAbiertos = sorteos.length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard Banca</h1>
          <p className="text-gray-500">Banca La Fortuna - Resumen general</p>
        </div>
        <button
          onClick={fetchData}
          className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
          title="Refrescar"
        >
          <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Alerta sorteos pendientes de ganador */}
      {pendientes.length > 0 && (
        <div className="p-4 bg-red-50 border-2 border-red-300 rounded-xl">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <AlertTriangle size={24} className="text-red-600 shrink-0" />
              <div>
                <p className="font-bold text-red-700">{pendientes.length} sorteo(s) sin ganador asignado</p>
                <p className="text-sm text-red-600">Esto afecta el calculo de saldos. Asigne los numeros ganadores.</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/banca/premios')}
              className="w-full sm:w-auto px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium whitespace-nowrap text-center"
            >
              Ir a Premios
            </button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {pendientes.map(p => (
              <span key={`${p.id}-${p.fecha}`} className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium">
                {p.nombre} ({p.fecha})
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <DollarSign className="w-6 h-6" />
            </div>
            <div>
              <p className="text-blue-100 text-sm">Ventas operativas</p>
              <p className="text-xl sm:text-2xl font-bold">₡{ventasHoy.toLocaleString()}</p>
            </div>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Trophy className="w-6 h-6" />
            </div>
            <div>
              <p className="text-red-100 text-sm">Premios financieros (incluye histórico)</p>
              <p className="text-xl sm:text-2xl font-bold">₡{Number(saldoBanca.premios || 0).toLocaleString()}</p>
            </div>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6" />
            </div>
            <div>
              <p className="text-emerald-100 text-sm">Saldo financiero (incluye histórico)</p>
              <p className="text-xl sm:text-2xl font-bold">₡{Number(saldoBanca.saldo || 0).toLocaleString()}</p>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
              <Store className="w-6 h-6 text-primary-600" />
            </div>
            <div>
              <p className="text-gray-500 text-sm">Locales Activos</p>
              <p className="text-2xl font-bold text-gray-900">{localesActivos || 0}</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-success-100 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6 text-success-600" />
            </div>
            <div>
              <p className="text-gray-500 text-sm">Tickets</p>
              <p className="text-2xl font-bold text-gray-900">{ticketsHoy}</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-warning-100 rounded-xl flex items-center justify-center">
              <Trophy className="w-6 h-6 text-warning-600" />
            </div>
            <div>
              <p className="text-gray-500 text-sm">Jugadas Activas</p>
              <p className="text-2xl font-bold text-gray-900">
                {jugadasActivas}
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-danger-100 rounded-xl flex items-center justify-center">
              <Calendar className="w-6 h-6 text-danger-600" />
            </div>
            <div>
              <p className="text-gray-500 text-sm">Vendedores</p>
              <p className="text-2xl font-bold text-gray-900">{vendedoresActivos}</p>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-4">
        <Card>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-danger-100 rounded-xl flex items-center justify-center">
              <Calendar className="w-6 h-6 text-danger-600" />
            </div>
            <div>
              <p className="text-gray-500 text-sm">Sorteos Abiertos</p>
              <p className="text-2xl font-bold text-gray-900">{sorteosAbiertos}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Sorteos abiertos */}
      <Card>
        <h3 className="font-semibold text-gray-900 mb-4">Sorteos Abiertos</h3>
        <div className="space-y-3">
          {sorteos.length === 0 && (
            <p className="text-gray-400 text-sm">{loading ? 'Cargando...' : 'Sin sorteos abiertos'}</p>
          )}
          {sorteos.map((s) => (
            <div key={s.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                <span className="font-medium">{s.nombre}</span>
              </div>
              <span className="text-sm text-gray-500">Cierre: {s.hora_cierre}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default BancaDashboardPage;
